/**
 * `/r/:token` — guest entrance to a Design Room (no account, no email): preview → name → cookie `rt_<roomId>` → the room.
 * Deliberately outside the app shell: no sidebar, no auth, `.mk` light palette for the join card.
 */
import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, Users } from 'lucide-react'
import { toast } from 'sonner'
import { Wordmark } from '#/components/layout/wordmark'
import { RoomView } from '#/components/room/room-view'
import { ApiError } from '#/lib/api'
import {
  useJoinRoom,
  useMyRooms,
  useRoom,
  useRoomInvitePreview,
} from '#/lib/api-room'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/r/$token')({
  head: () => ({
    meta: [
      { title: m.rm_join_title() },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: GuestRoomPage,
})

const DEFAULT_ACCENT = '#2757e6'

function GuestRoomPage() {
  const { token } = Route.useParams()
  const { data, isLoading, error } = useRoomInvitePreview(token)
  const invite = data?.invite
  // V2: the invite may be revoked/expired while the guest still holds a valid cookie → fall back to the rooms the cookies grant
  const previewFailed = !isLoading && (Boolean(error) || !invite)
  const mine = useMyRooms(previewFailed)
  const roomId = invite?.roomId ?? mine.data?.rooms[0]?.id ?? ''
  const [joined, setJoined] = useState(false)
  // if a cookie / session already grants access, skip the form
  const room = useRoom(roomId, Boolean(roomId))
  useEffect(() => {
    if (room.data) setJoined(true)
  }, [room.data])
  const join = useJoinRoom(token)
  const [name, setName] = useState('')
  const color =
    invite?.branding?.color && /^#[0-9a-fA-F]{6}$/.test(invite.branding.color)
      ? invite.branding.color
      : DEFAULT_ACCENT
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    join.mutate(name.trim(), {
      onSuccess: () => {
        setJoined(true)
        void room.refetch()
      },
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : m.rm_err_generic()),
    })
  }
  if (joined && room.data) {
    return (
      <div className="flex min-h-full flex-col bg-background text-foreground">
        <div className="flex h-11 items-center justify-between border-b px-3 sm:px-5">
          <Wordmark size="sm" to="/" />
          <span className="truncate text-xs text-muted-foreground">
            {invite?.workspaceName}
          </span>
        </div>
        <RoomView snap={room.data} live={room.live} shell="guest" />
      </div>
    )
  }
  return (
    <div className="mk flex min-h-full items-center justify-center px-5 py-12">
      <div className="mk-card-white w-full max-w-md p-6">
        {isLoading ? (
          <p className="text-sm" style={{ color: 'var(--mk-fg-muted)' }}>
            {m.st_loading()}
          </p>
        ) : error || !invite ? (
          mine.isLoading || (roomId && room.isLoading) ? (
            <p className="text-sm" style={{ color: 'var(--mk-fg-muted)' }}>
              {m.st_loading()}
            </p>
          ) : roomId && room.data ? (
            <>
              <h1 className="text-xl">{room.data.room.title}</h1>
              <p
                className="mt-2 text-sm"
                style={{ color: 'var(--mk-fg-muted)' }}
              >
                {m.rm_join_returning()}
              </p>
              <button
                type="button"
                className="mk-btn mk-btn-primary mt-4"
                style={{ background: color }}
                onClick={() => setJoined(true)}
              >
                {m.rm_join_open_room()}
              </button>
            </>
          ) : (
            <>
              <h1 className="text-xl">{m.rm_join_closed_title()}</h1>
              <p
                className="mt-2 text-sm"
                style={{ color: 'var(--mk-fg-muted)' }}
              >
                {m.rm_join_not_found()}
              </p>
            </>
          )
        ) : invite.expired ? (
          room.data ? (
            <>
              <h1 className="text-xl">{invite.title}</h1>
              <p
                className="mt-2 text-sm"
                style={{ color: 'var(--mk-fg-muted)' }}
              >
                {m.rm_join_returning()}
              </p>
              <button
                type="button"
                className="mk-btn mk-btn-primary mt-4"
                style={{ background: color }}
                onClick={() => setJoined(true)}
              >
                {m.rm_join_open_room()}
              </button>
            </>
          ) : (
            <>
              <h1 className="text-xl">{invite.title}</h1>
              <p
                className="mt-2 text-sm"
                style={{ color: 'var(--mk-fg-muted)' }}
              >
                {m.rm_join_expired()}
              </p>
            </>
          )
        ) : (
          <>
            <div className="flex items-center gap-3">
              {invite.branding?.logoUrl ? (
                <img
                  src={invite.branding.logoUrl}
                  alt=""
                  className="size-10 rounded-lg object-contain"
                />
              ) : (
                <span
                  className="inline-block size-10 rounded-lg"
                  style={{ background: color }}
                />
              )}
              <div className="min-w-0">
                <div
                  className="truncate text-sm font-semibold"
                  style={{ color }}
                >
                  {invite.branding?.name ?? invite.workspaceName}
                </div>
                <div
                  className="truncate text-xs"
                  style={{ color: 'var(--mk-fg-muted)' }}
                >
                  {m.rm_step_of({ n: invite.step })}
                </div>
              </div>
            </div>
            <h1 className="mt-4 text-xl">{m.rm_join_title()}</h1>
            <p
              className="mt-1.5 text-sm"
              style={{ color: 'var(--mk-fg-muted)' }}
            >
              {m.rm_join_desc({
                title: invite.title,
                workspace: invite.workspaceName,
              })}
            </p>
            {invite.goal ? (
              <p
                className="mt-2 rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--mk-border)' }}
              >
                <span
                  className="text-xs font-medium"
                  style={{ color: 'var(--mk-fg-muted)' }}
                >
                  {m.rm_brief_goal()}
                </span>
                <br />
                {invite.goal}
              </p>
            ) : null}
            <form className="mt-5 flex flex-col gap-3" onSubmit={submit}>
              <label
                className="text-xs font-medium"
                htmlFor="rm-guest-name"
                style={{ color: 'var(--mk-fg-muted)' }}
              >
                {m.rm_join_name()}
              </label>
              <input
                id="rm-guest-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={m.rm_join_name_ph()}
                minLength={2}
                maxLength={60}
                required
                autoComplete="name"
                className="h-11 rounded-lg border px-3 text-base outline-none focus:ring-2"
                style={{ borderColor: 'var(--mk-border)' }}
              />
              <button
                type="submit"
                disabled={join.isPending || name.trim().length < 2}
                className="mk-btn mk-btn-primary disabled:opacity-60"
                style={{ background: color }}
              >
                {join.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : null}{' '}
                {join.isPending ? m.rm_join_busy() : m.rm_join_btn()}
              </button>
            </form>
            <p
              className="mt-4 inline-flex items-center gap-1.5 text-xs"
              style={{ color: 'var(--mk-fg-muted)' }}
            >
              <Users className="size-3.5" />{' '}
              {m.rm_join_participants({ n: invite.participants })}
            </p>
          </>
        )}
        <p className="mt-6 text-xs" style={{ color: 'var(--mk-fg-faint)' }}>
          {m.rm_guest_footer()}{' '}
          <Wordmark to="/" size="sm" className="align-middle" />
        </p>
      </div>
    </div>
  )
}
